// Read-only catalog-browser models (MPN-1189). Mirrors tracked_data's catalog API
// (CatalogMetricSchema / VerticalSchema): every tracked metric a vertical defines,
// flattened with its entity-type and value-type context so authors can scan/search
// the whole catalog and recognize an existing metric before making a near-duplicate.

export interface CatalogVertical {
  id: number;
  code: string;
  name: string;
}

export interface CatalogDimension {
  id: number;
  code: string;
  name: string;
  description: string;
  reference_entity_type_code: string | null;
  values: string[]; // concrete values observed in the data (e.g. 2026FA, 2026SP)
}

// A field's business-role category (MPN-1240). Shared vocabulary across the catalog,
// the field-map editor, and the instructions generator. Names are disjoint from the
// campaign types / EngagementType (see the design doc "Field categories").
export type FieldCategory = "contact" | "measurement" | "targeting" | "system";

export const FIELD_CATEGORY_ORDER: FieldCategory[] = [
  "system",
  "contact",
  "measurement",
  "targeting",
];

export const FIELD_CATEGORY_LABEL: Record<FieldCategory, string> = {
  contact: "Contact",
  measurement: "Measurement",
  targeting: "Targeting",
  system: "System & keys",
};

// One-line data-tolerance explainer per category (exact copy from MPN-1240). Shown in the
// field-map editor group headers and the generated instructions doc.
export const FIELD_CATEGORY_TOLERANCE: Record<FieldCategory, string> = {
  contact:
    "Contact points (email, phone). Loaded even when incomplete; lower fill just means lower reach.",
  measurement:
    "KPIs — conversions & gates. Partial data can bias the metric, so it's integrity-gated.",
  targeting:
    "Targeting attributes (demographics, firmographics, academic). Watched for drops vs the field's own history.",
  system: "Identity key, as_of, names. Required — a record can't be processed without them.",
};

export interface CatalogMetric {
  id: number;
  entity_type_def_id: number;
  entity_type_code: string;
  entity_type_name: string;
  attr_key: string;
  display_name: string;
  description: string;
  category: FieldCategory;
  sample: string;
  value_type_id: number;
  value_type_code: string;
  base_kind: string;
  allowed_values: string[];
  validator: string;
  dimension_keys: string[];
  is_secure: boolean;
  searchable: boolean;
}

// ---------------------------------------------------------------------------
// Authoring (MPN-1207). The browser above reads flattened rows; authoring needs
// the raw catalog objects (FKs by id) that the write endpoints consume/return.
// ---------------------------------------------------------------------------

export interface CatalogEntityType {
  id: number;
  code: string;
  name: string;
  vertical_id: number;
  parent_type_id: number | null;
  is_reference: boolean;
}

export interface CatalogMetricType {
  id: number;
  code: string;
  name: string;
  base_kind: string;
  vertical_id: number | null; // null = base/global primitive; set = vertical-specific
  allowed_values: string[];
  validator: string;
}

export const BASE_KINDS = ["string", "bool", "decimal", "int", "date", "datetime"] as const;

// Write payloads — mirror the server *In schemas.
export interface VerticalIn { code: string; name: string; }
export interface EntityTypeIn {
  vertical_id: number;
  code: string;
  name: string;
  parent_type_id?: number | null;
  is_reference: boolean;
}
export interface MetricTypeIn {
  code: string;
  name: string;
  base_kind: string;
  vertical_id?: number | null;
  allowed_values: string[];
  validator: string;
}
export interface MetricIn {
  entity_type_def_id: number;
  attr_key: string;
  value_type_id: number;
  display_name: string;
  description: string;
  category: FieldCategory;
  sample: string;
  dimension_keys: string[];
  is_secure: boolean;
  searchable: boolean;
}

// A curated value on an axis, with the messy encodings that fold to it (MPN-1278). `aliases` is
// the whole point: fall26 / 26FA / 202608 all normalise to one canonical `code` at load time.
export interface DimensionValue {
  id: number;
  dimension_id: number;
  code: string;
  name: string;
  aliases: string[];
  sequence: number;
}

export interface DimensionValueIn {
  code: string;
  name: string;
  aliases: string[];
  sequence: number;
}

export interface DimensionIn {
  vertical_id: number;
  code: string;
  name: string;
  description: string;
  reference_entity_type_id?: number | null;
}

// Per-vertical doc-copy (sample + description) for the standard core fields that have no
// catalog metric — the identity key and contacts (MPN-1233). Keyed by (vertical, role).
export interface CatalogCoreFieldSample {
  id: number;
  vertical_id: number;
  role: string;
  sample: string;
  description: string;
}
export interface CoreFieldSampleIn {
  vertical_id: number;
  role: string;
  sample: string;
  description: string;
}
// The standard core roles the editor authors today; `role` is free-text server-side so
// firmographic account roles can be added later without a schema change.
export const CORE_FIELD_SAMPLE_ROLES = ["key", "email", "phone"] as const;

// ---------------------------------------------------------------------------
// Usage (MPN-1781). What a rename of a catalog item would touch. Rows are not the
// safety unit — dependents are: `rules` means a pricesheet rule or audience names the
// code and the server refuses the rename; `referenced` is config/data the rename
// fan-out handles; `unreferenced` renames freely.
// ---------------------------------------------------------------------------

export type UsageTier = "unreferenced" | "referenced" | "rules";

export interface UsageRuleRef {
  pricesheet_item_id: number;
  pricesheet_id: number;
  contract_id: number;
  contract_name: string;
  org_id: number;
  org_name: string;
}
export interface UsageConfigRef {
  ingest_definition_id: number;
  ingest_definition_name: string;
  org_id: number | null;
  org_name: string;
}
export interface UsageDataRef { org_id: number; org_name: string; rows: number; }

export interface ItemUsage {
  tier: UsageTier;
  rules: UsageRuleRef[];
  configs: UsageConfigRef[];
  data: UsageDataRef[];
  metrics: string[];    // dimensions only: attr_keys whose dimension_keys carry it
  audiences: string[];  // metrics only: audience definitions whose rules name it
}

export interface CatalogUsage {
  entity_types: Record<number, ItemUsage>;
  metrics: Record<number, ItemUsage>;
  dimensions: Record<number, ItemUsage>;
}
